import type { CashSummaryDto, InvoiceAgingDto } from '@hbs-us/contracts';
import { useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import type { ReportFormatters } from '../useReportFormatters.js';

/**
 * Outstanding-invoices card (legacy ReportsPage 193–250): Total Invoiced /
 * Collected / Still Owed, plus the overdue badge + open-invoice count + the
 * "View Invoices" link. Rendered ONLY when there is something open (mirrors
 * the legacy `outstanding > 0 || aging.openCount > 0` guard).
 *
 * Status colors map onto the locked tokens (UI-SPEC Color table): Collected →
 * `--chart-3` green, Still Owed → `--chart-4` amber, overdue badge →
 * `bg-destructive/10 text-destructive border-destructive/30`. The overdue
 * meaning is carried by the badge TEXT ("overdue"), not color alone (A11Y-01).
 *
 * The link carries `pointer-coarse:py-1` because the kit's tap-target floor only
 * reaches links inside a `<td>` and this one sits in a card footer: 20px tall is
 * under the 24px WCAG 2.5.8 asks for. Coarse-pointer only, so desktop is dense.
 */
export function OutstandingInvoices({
  cash,
  aging,
  fmt,
}: {
  cash: CashSummaryDto;
  aging: InvoiceAgingDto;
  fmt: ReportFormatters;
}) {
  const { t } = useTranslation();

  const invoiced = Number(cash.invoicedAmount || 0);
  const collected = Number(cash.collectedPayments || 0);
  const outstanding = Number(cash.openReceivables || 0);
  const overdueTotal = Number(aging.overdueTotal || 0);
  const openCount = Number(aging.openCount || 0);

  if (outstanding <= 0 && openCount <= 0) {
    return null;
  }

  return (
    <section className="mb-4 rounded-md border border-border bg-card p-6">
      <h2 className="mb-4 font-semibold text-foreground text-xl">
        {t('reports:outstanding.heading')}
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <div className="mb-1 text-muted-foreground text-xs">
            {t('reports:outstanding.totalInvoiced')}
          </div>
          <div className="font-mono font-semibold text-foreground text-xl">
            {fmt.money(invoiced)}
          </div>
        </div>
        <div>
          <div className="mb-1 text-muted-foreground text-xs">
            {t('reports:outstanding.collected')}
          </div>
          <div className="font-mono font-semibold text-success text-xl">{fmt.money(collected)}</div>
        </div>
        <div>
          <div className="mb-1 text-muted-foreground text-xs">
            {t('reports:outstanding.stillOwed')}
          </div>
          <div className="font-mono font-semibold text-warning text-xl">
            {fmt.money(outstanding)}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-border border-t pt-4">
        {overdueTotal > 0 ? (
          <span className="inline-flex items-center rounded-md border border-destructive/30 bg-destructive/10 px-3 py-1 font-mono font-semibold text-destructive text-sm">
            {t('reports:outstanding.overdueBadge', { amount: fmt.money(overdueTotal) })}
          </span>
        ) : null}
        <span className="text-muted-foreground text-sm">
          {t('reports:outstanding.openCount', { count: openCount })}
          {overdueTotal <= 0 ? ` · ${t('reports:outstanding.noneOverdue')}` : null}
        </span>
        <Link
          to="/invoices"
          className="ml-auto rounded-md font-medium text-foreground text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background pointer-coarse:py-1"
        >
          {t('reports:outstanding.viewInvoices')}
        </Link>
      </div>
    </section>
  );
}
